import {
  baseApi,
  getQueryArgs,
  throwErrorFromResponseData,
  transformErrorResponse,
} from 'store/api'
import { ApiResponse, getFile } from 'store/api/http'
import {
  detectConfiguredDevice,
  setConfigureDevice,
  setIsDeviceStatusVerified,
} from 'store/multiprofile/devices'
import { V4_CHECK_URL, VERIFY_DOMAIN, ANALYTICS_BASE_HOST_NAME } from 'gatsby-env-variables'
import {
  ApiResponseClient,
  DeviceClient,
  DeviceClientsInfoResponse,
  DeviceGroup,
  DeviceInfo,
  DeviceInfoResponse,
  DeviceSettings,
  DeviceTypesResponse,
  ModifyDevice,
  NewDevice,
  EndpointLastActivityMap,
  EndpointLastActivityData,
} from './devices.interface'
import { DetectionInfo } from '../configurationStatus'
import omit from 'lodash/omit'
import sortBy from 'lodash/sortBy'
import { current } from '@reduxjs/toolkit'
import { setIsOnboardingCompleted } from 'store/session'
import { ResolverStatus } from 'store/api/user/user.interface'
import isEqual from 'lodash/isEqual'
import { RootState } from 'store/rootReducer'

/**
 * Converts ISO 8601 timestamp string to Unix timestamp (seconds)
 */
const convertISOToUnixTimestamp = (isoString: string | undefined): number | undefined => {
  if (!isoString) {
    return undefined
  }
  return Math.floor(new Date(isoString).getTime() / 1000)
}

/**
 * Transforms client activity API response to activity data map
 * Returns a map of endpointId to { last_activity, clients: { [id]: { ts } } }
 */
const transformActivityResponse = (
  response: ApiResponse<{ items: EndpointLastActivityMap }>,
): Record<string, { last_activity?: number; clients?: Record<string, { ts: number }> }> => {
  const activityMap: Record<
    string,
    { last_activity?: number; clients?: Record<string, { ts: number }> }
  > = {}

  const items = response?.body?.items || {}

  Object.entries(items).forEach(
    ([endpointId, endpointData]: [string, EndpointLastActivityData]) => {
      activityMap[endpointId] = {
        last_activity: convertISOToUnixTimestamp(endpointData.lastActivityTime),
        clients: {},
      }

      if (endpointData.clients) {
        Object.entries(endpointData.clients).forEach(([clientId, clientData]) => {
          const clients = activityMap[endpointId].clients
          if (clients) {
            clients[clientId] = {
              ts: convertISOToUnixTimestamp(clientData.lastActivityTime) || 0,
            }
          }
        })
      }
    },
  )

  return activityMap
}

const scrubProfilesInDevice = (device: DeviceInfo): DeviceInfo => {
  const scrubbedDevice = {
    ...device,
    profile_id: device.profile_id?.toString(),
    profile_id2: device.profile_id2?.toString(),
  }
  if (scrubbedDevice.profile?.PK) {
    scrubbedDevice.profile = { ...scrubbedDevice.profile, PK: scrubbedDevice.profile.PK.toString() }
  }
  if (scrubbedDevice.profile2?.PK) {
    scrubbedDevice.profile2 = {
      ...scrubbedDevice.profile2,
      PK: scrubbedDevice.profile2.PK.toString(),
    }
  }
  return scrubbedDevice
}

const getClientsMapToArray = (
  clients?: ApiResponseClient | undefined,
): DeviceClient[] | undefined => {
  return clients
    ? Object.entries(clients).map(([id, value]) => ({ id, host: id, ...value } as DeviceClient))
    : clients
}

const getScrubbedDevice = device => {
  return {
    ...scrubProfilesInDevice(omit(device, ['clients']) as DeviceInfo),
    clients: getClientsMapToArray(device.clients) as DeviceClient[],
  }
}

export const getDeviceQrCode = (deviceId: string): Promise<Response> => {
  return getFile(`/devices/${deviceId}/qr-code`)
}

export const devicesApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getLatestActivity: builder.query({
      query: (region: string) =>
        getQueryArgs(`https://${region}.${ANALYTICS_BASE_HOST_NAME}/v2/client`),
      transformResponse: (response: ApiResponse<{ items: EndpointLastActivityMap }>) => {
        return transformActivityResponse(response)
      },
      transformErrorResponse,
    }),
    getDevices: builder.query({
      query: () => getQueryArgs('/devices'),
      transformResponse: (response: ApiResponse<{ devices: DeviceInfo<DeviceClient[]>[] }>) => {
        response.body.devices = response.body?.devices.map(device => {
          return getScrubbedDevice(device)
        })

        return response.body
      },
      transformErrorResponse,
      providesTags: () => ['Devices'],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled

          // Fetch latest activity after getting devices (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          if (activityData) {
            dispatch(
              devicesApi.util.updateQueryData('getDevices', '', draft => {
                draft.devices.forEach(device => {
                  const activity = activityData[device.device_id]
                  if (activity) {
                    device.last_activity = activity.last_activity

                    // Update client timestamps
                    if (device.clients && activity.clients) {
                      device.clients.forEach(client => {
                        const clientActivity = activity.clients?.[client.id]
                        if (clientActivity) {
                          client.ts = clientActivity.ts
                        }
                      })
                    }
                  }
                })
              }),
            )
          }
        } catch {}
      },
    }),
    getSubOrgDevices: builder.query({
      query: (orgId?: string) => getQueryArgs('/devices', undefined, undefined, orgId),
      transformResponse: (response: ApiResponse<{ devices: DeviceInfo<DeviceClient[]>[] }>) => {
        response.body.devices = response.body?.devices.map(device => {
          return getScrubbedDevice(device)
        })

        return response.body
      },
      transformErrorResponse,
      async onQueryStarted(orgId, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled

          // Fetch latest activity for org devices (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          if (activityData) {
            dispatch(
              devicesApi.util.updateQueryData('getSubOrgDevices', orgId, draft => {
                draft.devices.forEach(device => {
                  const activity = activityData[device.device_id]
                  if (activity) {
                    device.last_activity = activity.last_activity

                    // Update client timestamps
                    if (device.clients && activity.clients) {
                      device.clients.forEach(client => {
                        const clientActivity = activity.clients?.[client.id]
                        if (clientActivity) {
                          client.ts = clientActivity.ts
                        }
                      })
                    }
                  }
                })
              }),
            )
          }
        } catch {}
      },
    }),
    getDevice: builder.query({
      query: (deviceId?: string) => {
        return getQueryArgs(`/devices/${deviceId}`)
      },
      transformResponse: (response: ApiResponse<DeviceInfoResponse>) => response.body,
      transformErrorResponse,
      async onQueryStarted(deviceId, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled

          // Fetch latest activity after getting device (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const device = data.devices[0]
              const scrubbedDevice = getScrubbedDevice(device)

              const index = draft.devices.findIndex(d => d.PK === device.PK)

              if (
                draft.devices[index].status === ResolverStatus.UNVERIFIED &&
                scrubbedDevice.status === ResolverStatus.VERIFIED
              ) {
                dispatch(setIsDeviceStatusVerified(true))
              }

              // Merge activity data
              if (activityData && deviceId) {
                const activity = activityData[deviceId]
                if (activity) {
                  scrubbedDevice.last_activity = activity.last_activity

                  // Update client timestamps
                  if (scrubbedDevice.clients && activity.clients) {
                    scrubbedDevice.clients.forEach(client => {
                      const clientActivity = activity.clients?.[client.id]
                      if (clientActivity) {
                        client.ts = clientActivity.ts
                      }
                    })
                  }
                }
              }

              if (!isEqual(draft.devices[index], scrubbedDevice)) {
                draft.devices.splice(index, 1, scrubbedDevice)
              }
            }),
          )
        } catch {}
      },
    }),
    getDeviceClients: builder.query({
      query: (deviceId?: string) => {
        return getQueryArgs(`/devices/${deviceId}/clients`)
      },
      transformResponse: (response: ApiResponse<DeviceClientsInfoResponse>) => ({
        ...response.body,
        clients: getClientsMapToArray(response.body?.clients) ?? [],
      }),
      transformErrorResponse,
      async onQueryStarted(deviceId, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled

          // Fetch latest activity after getting clients (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const clientsToSet = data.clients

              const index = draft.devices.findIndex(d => d.device_id === deviceId)

              // Merge activity data with clients
              if (activityData && deviceId) {
                const activity = activityData[deviceId]
                if (activity) {
                  draft.devices[index].last_activity = activity.last_activity

                  // Update client timestamps
                  if (activity.clients) {
                    clientsToSet.forEach(client => {
                      const clientActivity = activity.clients?.[client.id]
                      if (clientActivity) {
                        client.ts = clientActivity.ts
                      }
                    })
                  }
                }
              }

              draft.devices[index] =
                clientsToSet.length > 0
                  ? { ...draft.devices[index], clients: clientsToSet }
                  : omit(draft.devices[index], ['clients'])
            }),
          )
        } catch {}
      },
    }),
    putDevice: builder.mutation({
      query: ({ PK, device }: { PK: string; device: ModifyDevice }) =>
        getQueryArgs(
          `${device.legacy_ipv4_status ? V4_CHECK_URL : ''}/devices/${PK}`,
          'PUT',
          device,
        ),
      transformResponse: (response: ApiResponse<DeviceInfo<ApiResponseClient>>) => {
        throwErrorFromResponseData(response)

        return response.body
      },
      transformErrorResponse,
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled

          // Fetch latest activity after device update (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const modifiedDevice = getScrubbedDevice(data)

              const index = draft.devices.findIndex(d => d.PK === modifiedDevice.PK)

              // Merge activity data
              if (activityData && draft.devices[index]) {
                const activity = activityData[draft.devices[index].device_id]
                if (activity) {
                  modifiedDevice.last_activity = activity.last_activity

                  // Update client timestamps
                  const deviceClients = draft.devices[index].clients
                  if (deviceClients && activity.clients) {
                    deviceClients.forEach(client => {
                      const clientActivity = activity.clients?.[client.id]
                      if (clientActivity) {
                        client.ts = clientActivity.ts
                      }
                    })
                  }
                }
              }

              draft.devices[index] = {
                ...modifiedDevice,
                last_activity: modifiedDevice.last_activity || draft.devices[index]?.last_activity,
                clients: draft.devices[index]?.clients,
                ctrld: modifiedDevice.ctrld
                  ? {
                      ...modifiedDevice.ctrld,
                      ...(draft.devices[index].ctrld?.version_latest && {
                        version_latest: draft.devices[index].ctrld?.version_latest,
                      }),
                    }
                  : draft.devices[index].ctrld,
              }
            }),
          )
        } catch {}
      },
    }),
    postDevice: builder.mutation({
      query: ({ newDevice }: { newDevice: NewDevice }) =>
        getQueryArgs(
          `${newDevice.legacy_ipv4_status ? V4_CHECK_URL : ''}/devices`,
          'POST',
          newDevice,
        ),
      transformResponse: (response: ApiResponse<DeviceInfo>) => {
        throwErrorFromResponseData(response)

        return response.body
      },
      transformErrorResponse,
      async onQueryStarted({}, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled

          // Fetch latest activity after device creation (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const newDevice = scrubProfilesInDevice(data)

              // Merge activity data
              if (activityData) {
                const activity = activityData[newDevice.device_id]
                if (activity) {
                  newDevice.last_activity = activity.last_activity
                }
              }

              draft.devices = sortBy([...draft.devices, newDevice], device => `${device.name}`)
            }),
          )
        } catch {}
      },
    }),
    deleteDevice: builder.mutation({
      query: ({ pk }: { pk: string }) => getQueryArgs(`/devices/${pk}`, 'DELETE'),
      transformResponse: (response: ApiResponse<[]>) => response.body,
      transformErrorResponse,
      async onQueryStarted({ pk }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled

          // Fetch latest activity for remaining devices (only if region is set)
          const region = (getState() as RootState).session.region
          const activityData = region
            ? (await dispatch(devicesApi.endpoints.getLatestActivity.initiate(region)))?.data
            : undefined

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              draft.devices = draft.devices.filter(d => d.PK !== pk)

              // Update activity for remaining devices
              if (activityData) {
                draft.devices.forEach(device => {
                  const activity = activityData[device.device_id]
                  if (activity) {
                    device.last_activity = activity.last_activity

                    // Update client timestamps
                    if (device.clients && activity.clients) {
                      device.clients.forEach(client => {
                        const clientActivity = activity.clients?.[client.id]
                        if (clientActivity) {
                          client.ts = clientActivity.ts
                        }
                      })
                    }
                  }
                })
              }
            }),
          )
        } catch {}
      },
    }),
    detectDevice: builder.query({
      query: () => {
        const randomSubDomain = Math.random().toString(36).substring(2, 12)

        return getQueryArgs(`https://${randomSubDomain}.${VERIFY_DOMAIN}/detect`, 'GET')
      },
      transformResponse: (response: ApiResponse<DetectionInfo>) => {
        return response.body
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const configuredDevice = draft.devices.find(d => d.PK === data.uid)
              if (configuredDevice) {
                if (configuredDevice.status === ResolverStatus.UNVERIFIED) {
                  configuredDevice.status = ResolverStatus.VERIFIED
                }
                dispatch(setConfigureDevice(current(configuredDevice)))
              } else {
                dispatch(setConfigureDevice(undefined))
              }

              dispatch(setIsOnboardingCompleted(true))
              dispatch(detectConfiguredDevice(false))
            }),
          )
        } catch {
          dispatch(setConfigureDevice(undefined))
        }
      },
    }),
    getDevicesTypes: builder.query({
      query: () => getQueryArgs('/devices/types/new', 'GET'),
      transformResponse: (
        response: ApiResponse<{
          types: {
            group: DeviceGroup
            icons: DeviceSettings[]
            name: string
          }[]
        }>,
      ) => {
        response.body.types = Object.entries(
          (response.body.types ?? {}) as DeviceTypesResponse,
        ).map(([id, value]) => ({
          group: id as DeviceGroup,
          ...value,
          //@ts-ignore
          icons: Object.entries(value.icons).map(([id, v]) => ({ type: id, ...v })),
        }))

        return response.body
      },
      transformErrorResponse,
    }),
    deleteDeviceClient: builder.mutation({
      query: ({ deviceId, clientId }: { deviceId: string; clientId: string }) => {
        return getQueryArgs(`/devices/${deviceId}/${clientId}`, 'DELETE')
      },
      transformResponse: (response: ApiResponse<[]>) => response.body,
      transformErrorResponse,
      async onQueryStarted({ deviceId, clientId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            devicesApi.util.updateQueryData('getDevices', '', draft => {
              const device = draft?.devices.find(d => d.PK === deviceId)

              if (device) {
                device.clients = device?.clients?.filter(c => c.id !== clientId)
              }
            }),
          )

          if (deviceId) {
            dispatch(
              devicesApi.util.updateQueryData('getDeviceClients', deviceId, draft => {
                draft.clients = draft?.clients?.filter(c => c.id !== clientId)
              }),
            )
          }
        } catch {}
      },
    }),
  }),
})

export const {
  endpoints,
  useGetDevicesQuery,
  usePutDeviceMutation,
  usePostDeviceMutation,
  useDeleteDeviceMutation,
  useDeleteDeviceClientMutation,
  useGetDevicesTypesQuery,
  useGetDeviceClientsQuery,
  useDetectDeviceQuery,
  useGetSubOrgDevicesQuery,
} = devicesApi
